import { isBeforeDay, isSameDay, startOfMonth } from "../Calendar/dates.js";
import { Calendar } from "../Calendar/Calendar.js";
import { formatDate } from "../../lib/format.js";
import { useFormFieldControl } from "../FormField/FormFieldContext.js";
import { DateField } from "../_dateField/DateField.js";
import { parseTypedDate } from "../_dateField/typed.js";
import { forwardRef, useId, useState } from "react";
import { jsx } from "react/jsx-runtime";
//#region src/components/DatePicker/DatePicker.tsx
/** What typed entry reads and shows as its placeholder. */
var TYPED_FORMAT = "MM/DD/YYYY";
/** "09/15/2026": the local day of the date, as `formatDate` shows a pay date. */
var formatPickerDate = (date) => formatDate({
	year: date.getFullYear(),
	month: date.getMonth() + 1,
	day: date.getDate()
});
/**
* A date field: a button showing the date that opens a Calendar in a popover. Picking a
* day closes it. With `allowTextInput`, a text input and a calendar button instead. The
* ref goes to the button (the calendar button when typed); `className` goes on the
* wrapper.
*/
var DatePicker = forwardRef(function DatePicker({ value, defaultValue = null, onChange, allowTextInput = false, placeholder = allowTextInput ? TYPED_FORMAT : "Select date", min = null, max = null, disabled = false, size = "md", format = formatPickerDate, clearable = true, invalid: invalidProp, id: idProp, "aria-label": ariaLabel, "aria-describedby": describedByProp, today, defaultOpen = false, className }, ref) {
	const generatedId = useId();
	const { id = `ds-datepicker-${generatedId}`, "aria-describedby": describedBy, invalid } = useFormFieldControl({
		id: idProp,
		"aria-describedby": describedByProp,
		invalid: invalidProp
	});
	const [internal, setInternal] = useState(defaultValue);
	const current = value === void 0 ? internal : value;
	const commit = (next) => {
		if (value === void 0) setInternal(next);
		onChange?.(next);
	};
	const [open, setOpen] = useState(defaultOpen);
	const [month, setMonth] = useState(() => startOfMonth(current ?? today ?? /* @__PURE__ */ new Date()));
	return /* @__PURE__ */ jsx(DateField, {
		id,
		size,
		invalid,
		disabled,
		valueText: current === null ? null : format(current),
		placeholder,
		describedBy,
		"aria-label": ariaLabel,
		clearLabel: clearable && current !== null ? `Clear date, ${format(current)}` : null,
		onClear: () => commit(null),
		open,
		onOpenChange: (next) => {
			if (next) setMonth(startOfMonth(current ?? today ?? /* @__PURE__ */ new Date()));
			setOpen(next);
		},
		popoverLabel: "Choose a date",
		triggerRef: ref,
		className,
		typed: allowTextInput ? {
			text: current === null ? "" : format(current),
			onCommit: (text) => {
				if (text.trim() === "") {
					if (clearable) commit(null);
					return true;
				}
				const day = parseTypedDate(text);
				if (!day || min && isBeforeDay(day, min) || max && isBeforeDay(max, day)) return false;
				if (!isSameDay(day, current)) commit(day);
				return true;
			},
			placeholder,
			hint: `Format ${TYPED_FORMAT}`,
			calendarLabel: "Choose date",
			size: 10
		} : void 0,
		children: /* @__PURE__ */ jsx(Calendar, {
			month,
			onMonthChange: setMonth,
			selected: current,
			min,
			max,
			...today && { today },
			onSelect: (day) => {
				commit(day);
				setOpen(false);
			}
		})
	});
});
//#endregion
export { DatePicker, TYPED_FORMAT, formatPickerDate };

//# sourceMappingURL=DatePicker.js.map